import { useWorkspaceText } from '../interface-copy';
import { useState } from 'react';
import { Dialog } from 'radix-ui';
import { CirclePlayIcon } from 'lucide-react';
import { formatCopy } from '@/i18n/ns';
import { useWorkspace } from '../context';
import { CanvasHeaderTools } from '../canvas-footer';
import { AccessPanel } from './access';
import { audienceLabel } from '../sharing-api';
import { emptyTurnContext } from '../types';
import { useIntlLocale } from '../intl-locale';
import {
  AudienceControl,
  PeopleList,
  PersonRow,
  SheetFooter,
  SheetNote,
  SheetSection,
  ShareSheet,
  ShareTrigger,
  audienceStatus,
  roleOptions,
  sameAudience,
  toSheetAudience,
  toVisibility,
  useColleagues,
  useSheetCopy,
  type SheetAudience,
  type SheetMember,
  type SheetRole,
} from '../share-sheet/share-sheet';
import { RunsOn, ViewerShareButton } from '../share-sheet/runs-on';
import { agentsApi, keepsAccess, type Agent } from './api';
import { CLOSES_CANVAS, useIsCompanyAdmin, type Act } from './shared';

// An Agent's Share button (operator 2026-10-09): the same sheet as every other
// item. People who can edit it choose who can use or edit it; everyone sees
// what it runs on. Its connections change only in its chat.

export function AgentShareChip({
  agent,
  busy,
  act,
  accessOpen,
  onAccessOpen,
}: {
  agent: Agent;
  busy: boolean;
  act: Act;
  accessOpen: boolean;
  onAccessOpen: (open: boolean) => void;
}) {
  return (
    <CanvasHeaderTools>
      <AgentShareButton
        agent={agent}
        busy={busy}
        act={act}
        open={accessOpen}
        setOpen={onAccessOpen}
      />
    </CanvasHeaderTools>
  );
}

function AgentShareButton({
  agent,
  busy,
  act,
  open,
  setOpen,
}: {
  agent: Agent;
  busy: boolean;
  act: Act;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const ws = useWorkspace();
  const copy = useSheetCopy();
  const locale = useIntlLocale();
  const label = audienceLabel({ visibility: agent.sharing.visibility }, ws.companyName, locale);
  if (!agent.access.edit)
    return agent.sharing.visibility === 'private' ? (
      <small className="ws-muted">{label}</small>
    ) : (
      <ViewerShareButton
        kind="agent"
        id={agent.id}
        title={agent.name}
        thing="theAgent"
        ownerName={agent.owner?.name}
        roleLabel={agent.access.run ? copy.canUse : copy.canView}
        selfId={ws.user?.id}
        audienceLabel={label}
        testId="agent-share-viewer"
      />
    );
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !busy && setOpen(next)}>
      <Dialog.Trigger asChild>
        <ShareTrigger
          visibility={agent.sharing.visibility}
          audienceLabel={label}
          testId="agent-share"
          disabled={busy}
        />
      </Dialog.Trigger>
      {open && (
        <AgentShareSheet
          key={JSON.stringify(agent.sharing)}
          agent={agent}
          busy={busy}
          act={act}
          close={() => setOpen(false)}
        />
      )}
    </Dialog.Root>
  );
}

function AgentShareSheet({
  agent,
  busy,
  act,
  close,
}: {
  agent: Agent;
  busy: boolean;
  act: Act;
  close: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const admin = useIsCompanyAdmin();
  // Only its owner or an admin makes it private, and never one whose owner left.
  const keepPrivate = Boolean(agent.owner) && (agent.owner?.userId === ws.user?.id || admin);
  const copy = useSheetCopy();
  const locale = useIntlLocale();
  const [audience, setAudience] = useState<SheetAudience>(
    toSheetAudience(agent.sharing.visibility),
  );
  const [members, setMembers] = useState<SheetMember[]>(
    agent.sharing.audience.flatMap((entry) =>
      entry.userId ? [{ userId: entry.userId, role: entry.role }] : [],
    ),
  );
  const [companyRole, setCompanyRole] = useState<SheetRole>(agent.sharing.companyRole ?? 'viewer');
  const colleagues = useColleagues();
  const visibility = toVisibility(audience);
  const next = {
    visibility,
    audience: visibility === 'members' ? members : [],
    ...(visibility === 'company' ? { companyRole } : {}),
  };
  const dirty =
    visibility !== agent.sharing.visibility ||
    (visibility === 'company' && companyRole !== (agent.sharing.companyRole ?? 'viewer')) ||
    (visibility === 'members' &&
      !sameAudience(
        members,
        agent.sharing.audience.flatMap((entry) =>
          entry.userId ? [{ userId: entry.userId, role: entry.role }] : [],
        ),
      ));
  const save = () =>
    void act(async () => {
      const result = await agentsApi.updateSharing(agent.id, next);
      close();
      // Someone who can no longer open it after the change leaves its canvas.
      return keepsAccess(result) ? result : CLOSES_CANVAS;
    }, copy.updated);
  const changeInChat = () => {
    close();
    ws.send(
      formatCopy(copy.changesPrompt, { kind: 'agent', id: agent.id, name: agent.name }),
      emptyTurnContext(),
      true,
      null,
      { newChat: true },
    );
  };
  const roles = roleOptions(copy, 'work');
  return (
    <ShareSheet
      eyebrow={copy.eyebrowShare}
      title={agent.name}
      icon={<CirclePlayIcon />}
      busy={busy}
      testId="agent-share-sheet"
      status={
        dirty
          ? copy.statusPending
          : audienceStatus(copy, locale, audience, members.length, ws.companyName)
      }
      actions={
        <SheetFooter
          dirty={dirty}
          busy={busy}
          disabled={visibility === 'members' && !members.length}
          saveTestId="agent-share-save"
          onSave={save}
        />
      }
    >
      <SheetSection title={copy.whoTitle}>
        <AudienceControl
          value={audience}
          companyName={ws.companyName}
          disabled={busy}
          reasons={
            keepPrivate ? {} : { private: agent.owner ? copy.privateNeedsOwner : copy.agentNoOwner }
          }
          onChange={setAudience}
        />
        {audience === 'private' && agent.sharing.visibility !== 'private' && (
          <SheetNote testId="agent-share-personal">{copy.agentPersonal}</SheetNote>
        )}
        {audience === 'company' && (
          <PersonRow
            id="company"
            company
            name={formatCopy(copy.everyone, { company: ws.companyName })}
            detail={copy.everyoneRowBody}
            role={companyRole}
            roles={roles}
            disabled={busy}
            onRole={setCompanyRole}
          />
        )}
        {audience === 'people' && (
          <PeopleList
            members={members}
            colleagues={colleagues.people}
            loading={colleagues.loading}
            failed={colleagues.failed}
            roles={roles}
            exclude={[agent.owner?.userId, ws.user?.id].filter((id): id is string => Boolean(id))}
            disabled={busy}
            fixed={
              agent.owner ? (
                <PersonRow
                  id={agent.owner.userId}
                  name={
                    agent.owner.userId === ws.user?.id
                      ? formatCopy(copy.you, { name: agent.owner.name ?? '' })
                      : (agent.owner.name ?? copy.fromColleague)
                  }
                  fixedLabel={copy.owner}
                />
              ) : null
            }
            onChange={setMembers}
          />
        )}
      </SheetSection>
      <SheetSection title={uiText('Editors and access')}>
        <div className="ws-agents-access">
          <AccessPanel agent={agent} busy={busy} act={act} />
        </div>
      </SheetSection>
      <RunsOn
        kind="agent"
        id={agent.id}
        thing="theAgent"
        selfId={ws.user?.id}
        chatLabel={copy.editInChat}
        onChangeInChat={changeInChat}
      />
    </ShareSheet>
  );
}
